import React, { useState, useCallback } from "react";
import { useNavigate, useParams, Link as RouterLink } from "react-router-dom";
import axios from "axios";
import {
  Stack,
  Button,
  IconButton,
  OutlinedInput,
  InputLabel,
  InputAdornment,
  FormControl,
  Typography,
  Box,
  Paper,
  Snackbar,
  Alert,
  Link,
} from "@mui/material";
import { LockReset, Visibility, VisibilityOff } from "@mui/icons-material";
import { flattenDjangoRestError, copy as userCopy } from "../utils/userFacingMessages";

/** Step 2 of the password reset, opened from the link in the email:
 *  /passwordreset/<uid>/<token>. Posts the new password with the token. */
function PasswordReset() {
  const navigate = useNavigate();
  const { uid, token } = useParams();

  const [password1, setPassword1] = useState("");
  const [password2, setPassword2] = useState("");
  const [showPassword1, setShowPassword1] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastSeverity, setToastSeverity] = useState("success");
  const [toastMessage, setToastMessage] = useState("");

  const showToast = useCallback((message, severity = "success") => {
    setToastMessage(message);
    setToastSeverity(severity);
    setToastOpen(true);
  }, []);

  const handleCloseToast = (_, reason) => {
    if (reason === "clickaway") return;
    setToastOpen(false);
  };

  const preventFocusLoss = (event) => {
    event.preventDefault();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    const data = {
      uid,
      token,
      new_password1: password1,
      new_password2: password2,
    };
    const url =
      "api/account/password_reset_confirm/" +
      encodeURIComponent(uid) +
      "/" +
      encodeURIComponent(token) +
      "/";
    try {
      const res = await axios.post(url, data);
      if (res.status === 200) {
        showToast(userCopy.passwordUpdated, "success");
        setTimeout(() => navigate("/login"), 1500);
      } else {
        showToast(userCopy.resetPasswordFailed, "error");
      }
    } catch (err) {
      console.error("Password reset failed", err);
      const raw = err?.response?.data;
      let msg = null;
      if (raw && typeof raw === "object" && (raw.token || raw.uid)) {
        msg = userCopy.resetLinkInvalid;
      }
      if (!msg) {
        msg = flattenDjangoRestError(raw);
      }
      if (!msg && !err.response) {
        msg = userCopy.profileSaveNetwork;
      }
      if (!msg) {
        msg = userCopy.resetPasswordFailed;
      }
      showToast(msg, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const passwordField = (id, label, value, setValue, shown, setShown) => (
    <FormControl fullWidth variant="outlined">
      <InputLabel htmlFor={id}>{label}</InputLabel>
      <OutlinedInput
        required
        id={id}
        label={label}
        type={shown ? "text" : "password"}
        value={value}
        onChange={(event) => setValue(event.target.value)}
        autoComplete="new-password"
        endAdornment={
          <InputAdornment position="end">
            <IconButton
              aria-label={"toggle " + label.toLowerCase() + " visibility"}
              onClick={() => setShown(!shown)}
              onMouseDown={preventFocusLoss}
              edge="end"
            >
              {shown ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </InputAdornment>
        }
      />
    </FormControl>
  );

  return (
    <Stack
      direction="column"
      justifyContent="center"
      alignItems="center"
      spacing={4}
      sx={{ mt: "2%", mb: 4 }}
    >
      <Box sx={{ width: "100%", maxWidth: "1150px", px: 2, boxSizing: "border-box" }}>
        <Stack
          sx={{
            width: "100%",
            py: 2,
            borderRadius: 3,
            background: "linear-gradient(135deg, #f5f6f8 0%, #ecedf1 100%)",
            border: "1px solid #dde1e7",
            boxSizing: "border-box",
          }}
        >
          <Box sx={{ px: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              New password
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Choose a new password for your account.
            </Typography>
          </Box>
        </Stack>

        <Paper
          component="form"
          onSubmit={handleSubmit}
          elevation={0}
          sx={{
            width: "100%",
            mt: 4,
            mb: 2,
            borderRadius: 3,
            border: "1px solid #e7ebf3",
            backgroundColor: "#ffffff",
            p: { xs: 2, md: 3 },
            boxSizing: "border-box",
          }}
        >
          <Stack spacing={2.5} sx={{ maxWidth: "540px" }}>
            {passwordField(
              "reset-password1",
              "New password",
              password1,
              setPassword1,
              showPassword1,
              setShowPassword1
            )}
            {passwordField(
              "reset-password2",
              "Repeat new password",
              password2,
              setPassword2,
              showPassword2,
              setShowPassword2
            )}

            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: -0.5 }}>
              <Link
                component={RouterLink}
                to="/passwordforgotten"
                underline="hover"
                variant="body2"
                sx={{ fontWeight: 600 }}
              >
                Request a new link
              </Link>
            </Box>

            <Button
              type="submit"
              variant="contained"
              disabled={submitting}
              startIcon={<LockReset />}
              sx={{
                height: "52px",
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 700,
                alignSelf: { xs: "stretch", sm: "flex-start" },
                px: 3,
                mt: 0.5,
              }}
            >
              {submitting ? "Saving…" : "Set password"}
            </Button>
          </Stack>
        </Paper>
      </Box>

      <Snackbar
        open={toastOpen}
        autoHideDuration={6000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseToast}
          severity={toastSeverity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>
    </Stack>
  );
}

export default PasswordReset;
